import { useEffect, useState } from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { Schedule, WarningAmber } from "@mui/icons-material";

import { API_URL } from "../helpers/constants";
import { getToken } from "../helpers/tokenStorage";

/**
 * Trial-tier indicator for the AppBar.
 *
 * Fetches GET /api/accounts/me on mount; if the account is in trial
 * (billing_enabled=false), renders a small brand-orange chip with
 * days-left + a tooltip showing the current resource limits + when
 * the trial ends. Clicking the chip opens an "Upgrade" target.
 *
 * Renders null when:
 *   - no session token (unauthenticated public pages)
 *   - account fetch fails (don't block the AppBar)
 *   - billing is enabled (paid tier — chip serves no purpose)
 */
export default function TrialChip() {
  const [self, setSelf] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_URL}/accounts/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSelf)
      .catch(() => {});
  }, []);

  if (!self || self.billingEnabled) return null;

  const expired = !!self.trialExpired;
  const days = self.daysLeftInTrial;
  const label = expired
    ? "Trial expired"
    : days === 0
    ? "Trial · last day"
    : days === 1
    ? "Trial · 1 day left"
    : `Trial · ${days ?? "?"} days left`;

  const palette = expired
    ? { fg: "#ef4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.35)" }
    : (days != null && days <= 3)
    ? { fg: "#d97706", bg: "rgba(217,119,6,0.14)", border: "rgba(217,119,6,0.4)" }
    : { fg: "#c2410c", bg: "rgba(234,88,12,0.12)", border: "rgba(234,88,12,0.32)" };

  const tooltipBody = (
    <Box sx={{ p: 0.5, fontSize: "0.78rem", lineHeight: 1.5 }}>
      <Box sx={{ fontWeight: 600, mb: 0.5 }}>
        {expired
          ? "Your trial has ended — enable billing to keep your apps running."
          : "Trial account · single workspace, capped resources."}
      </Box>
      {self.trialEndOn && (
        <Box sx={{ opacity: 0.85 }}>
          Trial {expired ? "ended" : "ends"} on{" "}
          {new Date(self.trialEndOn).toLocaleDateString()}.
        </Box>
      )}
      <Box sx={{ mt: 0.75, opacity: 0.85 }}>
        Limits: {self.limits?.["limits.free_tier.projects"] ?? "?"} project
        {self.limits?.["limits.free_tier.projects"] === 1 ? "" : "s"},{" "}
        {self.limits?.["limits.free_tier.applications"] ?? "?"} apps,{" "}
        {self.limits?.["limits.free_tier.secrets"] ?? "?"} secrets.
      </Box>
      <Box sx={{ mt: 0.75, opacity: 0.7 }}>Click to upgrade.</Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipBody} arrow placement="bottom">
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        sx={{ display: "inline-flex" }}
      >
        <Chip
          icon={
            expired ? (
              <WarningAmber sx={{ fontSize: "0.95rem !important", color: `${palette.fg} !important` }} />
            ) : (
              <Schedule sx={{ fontSize: "0.95rem !important", color: `${palette.fg} !important` }} />
            )
          }
          label={label}
          onClick={() => {
            // Placeholder Upgrade target — wire to actual billing flow
            // when it ships. For now: in-app announcement.
            window.dispatchEvent(new CustomEvent("c2a:open-upgrade"));
          }}
          size="small"
          sx={{
            height: 26,
            fontSize: "0.72rem",
            fontWeight: 600,
            cursor: "pointer",
            color: palette.fg,
            background: palette.bg,
            border: `1px solid ${palette.border}`,
            borderRadius: 999,
            px: 0.5,
            "& .MuiChip-icon": { ml: 0.5, mr: -0.25 },
            "&:hover": {
              background: palette.bg.replace(/0\.\d+\)$/, "0.20)"),
              borderColor: palette.fg,
            },
          }}
        />
      </Box>
    </Tooltip>
  );
}
